import {useEffect, useRef, useState} from "react";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";

import {useShouldShowPriceByOfferConfigAndUser} from "../../../hooks/use_should_show_price_by_offer_config_and_user";
import {useGetPropertyDetailQuery} from "../../../property/api/get_property_detail";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IRecommendedProperty} from "../../../property/types/IRecommendedProperty";
import {isPropertyDetail} from "../../../property/utils/is_property_detail";
import {IValueLabel} from "../../../variables/types/IValueLabel";
import {trackTinderMultiLeadMediaButton, trackTinderMultiLeadOpenGallery, trackTinderMultiLeadViewItem} from "./tracking/track_tinder_multi_lead_events";
import {TinderMultiLeadHero} from "./TinderMultiLeadHero";
import {TinderMultiLeadInvestmentSection} from "./TinderMultiLeadInvestmentSection";
import {TinderMultiLeadLocationSection} from "./TinderMultiLeadLocationSection";
import {TinderMultiLeadMediaModal, type TinderMultiLeadMediaMode} from "./TinderMultiLeadMediaModal";
import {TinderMultiLeadParameterList} from "./TinderMultiLeadParameterList";

interface IProps {
    error: string | null;
    index: number;
    onReadyChange?: (propertyId: number, ready: boolean) => void;
    property: IPropertyDetail | IRecommendedProperty;
    variables: {
        offer_facilities?: IValueLabel[];
        offer_standard_description_type?: IValueLabel[];
        property_additional_areas?: IValueLabel[];
        property_kitchen_type?: IValueLabel[];
        property_quarters?: IValueLabel[];
    } | null;
}

export function TinderMultiLeadOfferCard({error, index, onReadyChange, property, variables}: IProps) {
    const shouldFetchPropertyDetail = !isPropertyDetail(property);
    const {
        data: propertyDetail,
        isFetching: isPropertyDetailFetching,
        isLoading: isPropertyDetailLoading
    } = useGetPropertyDetailQuery(property.id, {
        skip: !shouldFetchPropertyDetail
    });
    const displayProperty = propertyDetail || property;
    const isDisplayPropertyDetail = isPropertyDetail(displayProperty);
    const isDetailLoading = shouldFetchPropertyDetail && (isPropertyDetailLoading || isPropertyDetailFetching);
    const [mediaMode, setMediaMode] = useState<TinderMultiLeadMediaMode | null>(null);
    const viewItemTrackedRef = useRef<number | null>(null);

    // Tell the deck when this card's content has landed, so the height/scroll release never settles
    // onto a transient detail loader.
    useEffect(() => {
        onReadyChange?.(property.id, !isDetailLoading);
    }, [isDetailLoading, onReadyChange, property.id]);

    useEffect(() => {
        if (!isPropertyDetail(displayProperty) || viewItemTrackedRef.current === displayProperty.id) {
            return;
        }

        viewItemTrackedRef.current = displayProperty.id;
        trackTinderMultiLeadViewItem({property: displayProperty, index});
    }, [displayProperty, index]);

    const handleMainImageOpen = () => {
        setMediaMode("gallery");
        trackTinderMultiLeadOpenGallery({property: displayProperty, galleryView: "grid", photoSection: "gallery", mediaTabNavigation: "initial"});
    };

    const handleGalleryButtonClick = () => {
        setMediaMode("gallery");
        trackTinderMultiLeadMediaButton({property: displayProperty, kind: "gallery"});
    };

    const handlePlanButtonClick = () => {
        setMediaMode("plan");
        trackTinderMultiLeadMediaButton({property: displayProperty, kind: "plan"});
    };

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: displayProperty.offer.configuration.price_visibility,
        vendorId: displayProperty.offer.vendor.id,
        vendorPriceVisibility: displayProperty.offer.vendor.configuration.price_visibility
    });

    const parameterList = isDisplayPropertyDetail ? (
        <TinderMultiLeadParameterList property={displayProperty} variables={variables} />
    ) : (
        <div className={detailLoadingStyle}>
            {isDetailLoading ? (
                <>
                    <Loader size="sm" />
                    <Text variant="info_txt_1" color="var(--colors-gray-700)">
                        Ładujemy szczegóły oferty
                    </Text>
                </>
            ) : (
                <Text variant="info_txt_1" color="var(--colors-gray-700)">
                    Szczegóły tej oferty są chwilowo niedostępne.
                </Text>
            )}
        </div>
    );

    return (
        <div className={cardShellStyle}>
            <TinderMultiLeadHero
                property={displayProperty}
                shouldShowPrice={shouldShowPrice}
                parameterList={parameterList}
                onGalleryClick={handleGalleryButtonClick}
                onMainImageClick={handleMainImageOpen}
                onPlanClick={handlePlanButtonClick}
            />

            {isDisplayPropertyDetail && (
                <div className={bodyStyle}>
                    <TinderMultiLeadLocationSection property={displayProperty} />
                    <TinderMultiLeadInvestmentSection property={displayProperty} variables={variables} />
                </div>
            )}

            {error && (
                <div className={errorStyle}>
                    <Text variant="info_txt_1" align="center" color="var(--colors-danger)">
                        {error}
                    </Text>
                </div>
            )}

            {isDisplayPropertyDetail && mediaMode && (
                <TinderMultiLeadMediaModal mode={mediaMode} property={displayProperty} onClose={() => setMediaMode(null)} />
            )}
        </div>
    );
}

const cardShellStyle = css({
    display: "flex",
    flexDirection: "column",
    borderRadius: 2,
    overflow: "hidden",
    backgroundColor: "var(--colors-white)",
    boxShadow: "0 8px 20px rgba(174, 177, 180, 0.25)"
});

const bodyStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 3,
    px: 1.5,
    pb: 1.5,
    md: {
        px: 2,
        pb: 2
    }
});

const errorStyle = css({
    px: 1.5,
    pb: 1.5,
    md: {
        px: 2,
        pb: 2
    }
});

const detailLoadingStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 1,
    minHeight: 120,
    borderRadius: 2,
    backgroundColor: "var(--colors-gray-100)"
});
import {useEffect, useRef, useState} from "react";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";

import {useShouldShowPriceByOfferConfigAndUser} from "../../../hooks/use_should_show_price_by_offer_config_and_user";
import {useGetPropertyDetailQuery} from "../../../property/api/get_property_detail";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IRecommendedProperty} from "../../../property/types/IRecommendedProperty";
import {isPropertyDetail} from "../../../property/utils/is_property_detail";
import {IValueLabel} from "../../../variables/types/IValueLabel";
import {trackTinderMultiLeadMediaButton, trackTinderMultiLeadOpenGallery, trackTinderMultiLeadViewItem} from "./tracking/track_tinder_multi_lead_events";
import {TinderMultiLeadHero} from "./TinderMultiLeadHero";
import {TinderMultiLeadInvestmentSection} from "./TinderMultiLeadInvestmentSection";
import {TinderMultiLeadLocationSection} from "./TinderMultiLeadLocationSection";
import {TinderMultiLeadMediaModal, type TinderMultiLeadMediaMode} from "./TinderMultiLeadMediaModal";
import {TinderMultiLeadParameterList} from "./TinderMultiLeadParameterList";

interface IProps {
    error: string | null;
    index: number;
    onReadyChange?: (propertyId: number, ready: boolean) => void;
    property: IPropertyDetail | IRecommendedProperty;
    variables: {
        offer_facilities?: IValueLabel[];
        offer_standard_description_type?: IValueLabel[];
        property_additional_areas?: IValueLabel[];
        property_kitchen_type?: IValueLabel[];
        property_quarters?: IValueLabel[];
    } | null;
}

export function TinderMultiLeadOfferCard({error, index, onReadyChange, property, variables}: IProps) {
    const shouldFetchPropertyDetail = !isPropertyDetail(property);
    const {
        data: propertyDetail,
        isFetching: isPropertyDetailFetching,
        isLoading: isPropertyDetailLoading
    } = useGetPropertyDetailQuery(property.id, {
        skip: !shouldFetchPropertyDetail
    });
    const displayProperty = propertyDetail || property;
    const isDisplayPropertyDetail = isPropertyDetail(displayProperty);
    const isDetailLoading = shouldFetchPropertyDetail && (isPropertyDetailLoading || isPropertyDetailFetching);
    const [mediaMode, setMediaMode] = useState<TinderMultiLeadMediaMode | null>(null);
    const viewItemTrackedRef = useRef<number | null>(null);

    // Tell the deck when this card's content has landed, so the height/scroll release never settles
    // onto a transient detail loader.
    useEffect(() => {
        onReadyChange?.(property.id, !isDetailLoading);
    }, [isDetailLoading, onReadyChange, property.id]);

    useEffect(() => {
        if (!isPropertyDetail(displayProperty) || viewItemTrackedRef.current === displayProperty.id) {
            return;
        }

        viewItemTrackedRef.current = displayProperty.id;
        trackTinderMultiLeadViewItem({property: displayProperty, index});
    }, [displayProperty, index]);

    const handleMainImageOpen = () => {
        setMediaMode("gallery");
        trackTinderMultiLeadOpenGallery({property: displayProperty, galleryView: "grid", photoSection: "gallery", mediaTabNavigation: "initial"});
    };

    const handleGalleryButtonClick = () => {
        setMediaMode("gallery");
        trackTinderMultiLeadMediaButton({property: displayProperty, kind: "gallery"});
    };

    const handlePlanButtonClick = () => {
        setMediaMode("plan");
        trackTinderMultiLeadMediaButton({property: displayProperty, kind: "plan"});
    };

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: displayProperty.offer.configuration.price_visibility,
        vendorId: displayProperty.offer.vendor.id,
        vendorPriceVisibility: displayProperty.offer.vendor.configuration.price_visibility
    });

    const parameterList = isDisplayPropertyDetail ? (
        <TinderMultiLeadParameterList property={displayProperty} variables={variables} />
    ) : (
        <div className={detailLoadingStyle}>
            {isDetailLoading ? (
                <>
                    <Loader size="sm" />
                    <Text variant="info_txt_1" color="var(--colors-gray-700)">
                        Ładujemy szczegóły oferty
                    </Text>
                </>
            ) : (
                <Text variant="info_txt_1" color="var(--colors-gray-700)">
                    Szczegóły tej oferty są chwilowo niedostępne.
                </Text>
            )}
        </div>
    );

    return (
        <div className={cardShellStyle}>
            <TinderMultiLeadHero
                property={displayProperty}
                shouldShowPrice={shouldShowPrice}
                parameterList={parameterList}
                onGalleryClick={handleGalleryButtonClick}
                onMainImageClick={handleMainImageOpen}
                onPlanClick={handlePlanButtonClick}
            />

            {isDisplayPropertyDetail && (
                <div className={bodyStyle}>
                    <TinderMultiLeadLocationSection property={displayProperty} />
                    <TinderMultiLeadInvestmentSection property={displayProperty} variables={variables} />
                </div>
            )}

            {error && (
                <div className={errorStyle}>
                    <Text variant="info_txt_1" align="center" color="var(--colors-danger)">
                        {error}
                    </Text>
                </div>
            )}

            {isDisplayPropertyDetail && mediaMode && (
                <TinderMultiLeadMediaModal mode={mediaMode} property={displayProperty} onClose={() => setMediaMode(null)} />
            )}
        </div>
    );
}

const cardShellStyle = css({
    display: "flex",
    flexDirection: "column",
    borderRadius: 2,
    overflow: "hidden",
    backgroundColor: "var(--colors-white)",
    boxShadow: "0 8px 20px rgba(174, 177, 180, 0.25)"
});

const bodyStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 3,
    px: 1.5,
    pb: 1.5,
    md: {
        px: 2,
        pb: 2
    }
});

const errorStyle = css({
    px: 1.5,
    pb: 1.5,
    md: {
        px: 2,
        pb: 2
    }
});

const detailLoadingStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 1,
    minHeight: 120,
    borderRadius: 2,
    backgroundColor: "var(--colors-gray-100)"
});
